import type { SessionUserRole } from '@/store/auth/auth.dto';

/**
 * Who may do what to a vessel's plan.
 *
 * Answered per vessel from the caller's own role rows — `session.services`, one entry per
 * service with the rights it carries — rather than from `usePermissions`, which only ever
 * reports the *active* service. This page spans every vessel the caller belongs to, so the
 * active one is the wrong question.
 *
 * Rights, not roles. The plan is not the owner's alone: a captain can be the one paying for a
 * vessel that has no owner yet, and holds `GetSubscription` / `EditSubscription` for exactly
 * that — while an owner can withdraw either from a role per vessel, which no role name can
 * express.
 *
 * The three rights are named here rather than in `permissions.catalog`, which is also what
 * draws the Roles & Permissions grid: a name added there becomes a row on that page, and
 * these are not rows this change is adding.
 */

/** `GET /subscriptions` — the plan is readable. */
const GET_SUBSCRIPTION = 'GetSubscription';
/** `POST /subscriptions/checkout`, `PATCH|cancel|uncancel /subscriptions/{id}`. */
export const EDIT_SUBSCRIPTION = 'EditSubscription';

const VESSEL = 'vessel';

/** Whether the plan may be read. The write right implies it — the endpoints are separate. */
export const canViewPlan = (service: SessionUserRole): boolean =>
  !!service.rights?.some((right) => right === GET_SUBSCRIPTION || right === EDIT_SUBSCRIPTION);

/**
 * The vessels whose plan this person may at least see — what the account list is built from.
 *
 * Vessel services only: a service-provider account has roles of its own but no plan behind
 * them, and asking the vessel service for one by id would answer nothing.
 */
export const planManageableServices = (
  services: readonly SessionUserRole[] | undefined,
): SessionUserRole[] =>
  (services ?? []).filter((service) => service.service_type === VESSEL && canViewPlan(service));
